import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import AddInputs from "../../../../components/Add/AddInputs";
import S3ImageUpload from "../../../../components/S3uploads/S3ImageUpload";
import { BusinessDetailsTypes } from "./types";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { useDispatch } from "react-redux";
import { setAddLoading, setBusinessDetails, setEditBusinessDetails, setSavedData } from "../addMDSlice";
import validateMandatoryFields from "../../../../components/commonfunctions/validateMandatoryFields";
import useApi from "../../../../services/useApi";
import { showToast } from "../../../../components/toastMessage/toastSlice";

interface businessDetailsProps {
    handleNext: () => void;
    id: string;
    type: string;
}
// Define the type for the exposed functions
export interface businessDetailsRef {
    handleSubmit: () => void;
}
const BusinessDetails = forwardRef<businessDetailsRef, businessDetailsProps>(({ handleNext, id, type }, ref) => {

    //state for business details
    const { PatchAPI } = useApi();
    const getUrlPath = (type: any) => type?.toLowerCase().split(' ').join('-');

    const [businessDetailsErrors, setBusinessDetailsErrors] = useState<BusinessDetailsTypes>({
        'business_license_number': '',
        'store_name': '',
        'business_type': '',
        'business_valid_from': '',
        'business_valid_to': '',
        'business_license_front_page': '',
        'business_license_back_page': '',
    })
    const dispatch = useDispatch();
    const { businessDetails, user_id, savedData } = useSelector((state: RootState) => state.addMD);
    //options for business type
    const businessTypes = [
        "Super Store",
        "Retail Store",
        "Wholesale Store",
        "Handseller",
        "Logistic Company",
        "Services Company",
        "Distribution Company",
        "Transportation Company",
        "Payment Service Provider",
        "Exchange Services",
        "Petrol Pump Stations",
        "Restaurants",
        "Pharmacies",
        "Convenience Stores",
        "Mobile (On the Go) Agent",
        "FMCGs Companies"
    ]

    //for business details
    const bankDetailsInputs = {
        "": {
            "Store Name": {
                required: true,
                disabled: false,
                id: "store_name",
                type: "input",
                maxLength: 60,
                tabIndex: 0,
            },
            "Business Type": {
                required: true,
                disabled: false,
                id: "business_type",
                type: "dropDown",
                tabIndex: 2,
                noSearch: true,
                options: businessTypes,
            },
            "Business License Number ": {
                required: false,
                disabled: false,
                id: "business_license_number",
                type: "input",
                maxLength: 60,
                tabIndex: 0,
            },
            ...(businessDetails.business_license_number !== '' ? {
                "Business Valid From": {
                    required: true,
                    disabled: false,
                    id: "business_valid_from",
                    type: "date",
                    tabIndex: 2,
                },
                "Business Valid Up To": {
                    required: true,
                    disabled: false,
                    id: "business_valid_to",
                    type: "date",
                    tabIndex: 2,
                },
            } : {})
        },
    }

    // Handler for input tags
    const handleInputChanges = (key: string, value: string) => {
        if (
            (key === 'business_valid_from' || key === 'business_valid_to') &&
            businessDetailsErrors.business_valid_from === ' '
        ) {
            dispatch(setBusinessDetails({ key, value }));
            setBusinessDetailsErrors({
                ...businessDetailsErrors,
                business_valid_from: '',
                business_valid_to: '',
            });
            return;
        }

        if (key === 'business_license_number' && value === '') {
            const resetFields = ['business_valid_from', 'business_valid_to', 'business_license_front_page', 'business_license_back_page'];

            resetFields.forEach(field => dispatch(setBusinessDetails({ key: field, value: '' })));

            setBusinessDetailsErrors(prev => ({
                ...prev,
                ...resetFields.reduce((acc, field) => ({ ...acc, [field]: '' }), {}),
            }));

            dispatch(setBusinessDetails({ key, value }));
            return;
        }

        dispatch(setBusinessDetails({ key, value }));
        setBusinessDetailsErrors({ ...businessDetailsErrors, [key]: '' });
    };


    //submit handler for the business details
    /**
     * Handles submission of business details form
     * Validates mandatory fields, performs date range validation, and updates business details via API
     * Manages loading state and displays success/error toasts based on API response
     * 
     * @async
     * @returns {Promise<void>} Submits business details and navigates to next step on success
     */
    const handleSubmit = async () => {
        // Validate the fields for empty values and set the error messages
        let mandatoryFields = ['store_name', 'business_type'];
        if (businessDetails.business_license_number !== '') {
            mandatoryFields.push('business_valid_from', 'business_valid_to', 'business_license_front_page');
        }

        let errorObj = validateMandatoryFields(businessDetails, mandatoryFields);
        if (Object.keys(errorObj).length > 0) {
            setBusinessDetailsErrors({ ...businessDetailsErrors, ...errorObj });
            return;
        }

        // Checking for the date range validation
        if (new Date(businessDetails.business_valid_from) > new Date(businessDetails.business_valid_to)) {
            setBusinessDetailsErrors({
                ...businessDetailsErrors,
                business_valid_to: `'Business Valid Up To' should be greater than 'Business Valid From'`,
                business_valid_from: " ",
            });
            return;
        }

        function formatDate(isoString: any) {
            const date = new Date(isoString);
            return date.toISOString().split('T')[0];
          }
        // Creating the API payload after validation
        let payload = {
            business_details: {
                store_name: businessDetails.store_name,
                business_type: businessDetails.business_type,
                business_license_number: businessDetails.business_license_number,
                ...(businessDetails?.business_license_number !== '' && {
                    business_valid_from: businessDetails.business_valid_from ? (formatDate(businessDetails.business_valid_from)) : '',
                    business_valid_to: businessDetails.business_valid_to ? (formatDate(businessDetails.business_valid_to)) : '',
                    business_license_front_page: businessDetails.business_license_front_page,
                    business_license_back_page: businessDetails.business_license_back_page,
                }),
            },
        };
        if (id) {
            if(businessDetails.business_license_number === '') {
                payload.business_details = {
                    store_name: businessDetails.store_name,
                    business_type: businessDetails.business_type,
                    business_license_number: businessDetails.business_license_number,
                    business_valid_from: '',
                    business_valid_to: '',
                    business_license_front_page: '',
                    business_license_back_page: '',
                }
            }
        }

        // Calling the API
        dispatch(setAddLoading(true));
        let response = await PatchAPI(`${getUrlPath(type)}/${user_id}`, payload);

        if (!response.error) {
            dispatch(setSavedData({ key: 'business details', value: payload.business_details }));
            handleNext();
        } else {
            // Error handling
            dispatch(showToast({
                type: "error",
                message: response?.data?.response?.data?.message || 'Something went wrong',
            }));
        }

        dispatch(setAddLoading(false));
    };

    // Expose functions to parent
    useImperativeHandle(ref, () => ({
        handleSubmit,
    }));

    useEffect(() => {
        if (id || user_id) {
            const savedBankDetails = savedData?.['business details'] || [];
            dispatch(setEditBusinessDetails(savedBankDetails));
        }
    },[])
    return (
        <div className="pb-5">
            <AddInputs mapping={true} errors={businessDetailsErrors} totalInputCount={2} onInputChange={(id, value) => handleInputChanges(id, value)} inputs={bankDetailsInputs} inputValues={businessDetails} />
            {businessDetails.business_license_number !== '' && <div className="flex gap-10">
                <S3ImageUpload
                    heading=""
                    label="Business License Front Page"
                    disabled={false}
                    upload_id={'business_license_front_page'}
                    UploadPath={"uploads/business_license_front_page"}
                    handleUpload={(file, upload_id) => { handleInputChanges(file, upload_id) }}
                    uploadedFile={businessDetails?.business_license_front_page}
                    errorMessage={businessDetailsErrors?.business_license_front_page}
                    required={true}
                />
                <S3ImageUpload
                    heading=""
                    label="Business License Back Page"
                    disabled={false}
                    upload_id={'business_license_back_page'}
                    UploadPath={"uploads/business_license_back_page"}
                    handleUpload={(file, upload_id) => { handleInputChanges(file, upload_id) }}
                    uploadedFile={businessDetails?.business_license_back_page}
                    errorMessage={businessDetailsErrors?.business_license_back_page}
                    required={false}
                />
            </div>}
        </div>
    )
});

export default BusinessDetails